import React, { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import * as yup from 'yup';

type FormSingleValue = string | number | boolean | null | Date;
export type FormValue = FormSingleValue | FormSingleValue[];
export type FormValues = Record<string, FormValue>;

interface CreateFormStore<V extends FormValues = {}> {
  values: V;
  errors: Record<string, string>;
  schema?: yup.ObjectSchema<V>;
  onFieldChange: <T extends V = V>(name: keyof T, value: FormValue) => void;
  setValues: (values: V) => void;
  setSchema: (schema?: yup.ObjectSchema<V>) => void;
  onChangesMap: Record<keyof V, (value: FormValue) => void>;
  validate: (schema?: yup.ObjectSchema<V>) => Promise<boolean>;
}

interface FormOptions<V extends object = {}>
  extends Partial<{
    strategy: 'single-field' | 'all-fields';
    schema: yup.ObjectSchema<V>;
  }> {}

export const createFormStore = <V extends FormValues = {}>(
  initValues: V,
  options?: FormOptions<V>,
) => {
  const strategy = options?.strategy ?? 'all-fields';
  return create<CreateFormStore<V>>((set, get) => {
    const onChangesMap: Partial<CreateFormStore<V>['onChangesMap']> = {};

    Object.keys(initValues).forEach((key) => {
      const k = key as keyof V;
      onChangesMap[k] = (value: FormValue) => get().onFieldChange<V>(k, value);
    });

    return {
      values: initValues,
      errors: {} as Record<keyof V, string>,
      schema: options?.schema,

      onFieldChange: async (name, value) => {
        set((state) => ({
          ...state,
          values: { ...state.values, [name]: value },
        }));

        if (strategy === 'single-field') {
          const schema = get().schema;
          if (!schema) {
            return;
          }

          try {
            await schema.validateAt(name.toString(), get().values);
            set((s) => {
              const newErrors = { ...s.errors };
              delete newErrors[name as string];
              return { ...s, errors: newErrors };
            });
          } catch (err) {
            if (err instanceof yup.ValidationError) {
              set((s) => ({
                ...s,
                errors: { ...s.errors, [name]: err.message },
              }));
            }
          }
        }
      },

      setValues: (values) => {
        set((state) => ({
          ...state,
          values: { ...state.values, ...values },
        }));
      },

      onChangesMap: onChangesMap as Record<keyof V, (value: FormValue) => void>,

      setSchema: (schema) => {
        set({ schema });
      },

      validate: async (schema) => {
        const _schema = schema || get().schema;
        if (!_schema) {
          throw new Error('No schema provided for validation');
        }
        const values = get().values;

        try {
          await _schema.validate(values, { abortEarly: false });
          set((s) => ({ ...s, errors: {} }));
          return true;
        } catch (err) {
          if (err instanceof yup.ValidationError) {
            const errors: Record<string, string> = {};

            err.inner.forEach((e) => {
              if (e.path && !errors[e.path]) {
                errors[e.path] = e.message;
              }
            });
            set((s) => ({ ...s, errors }));
          }
          return false;
        }
      },
    };
  });
};

export const useForm = <V extends FormValues = {}>(
  initValues: V,
  options?: FormOptions<V>,
) => {
  const store = React.useMemo(() => createFormStore(initValues, options), []);

  const values = store(useShallow((state) => state.values));
  const errors = store(useShallow((state) => state.errors));
  const changeHandlers = store(useShallow((state) => state.onChangesMap));
  const onFieldChange = store((state) => state.onFieldChange);
  const setValues = store((state) => state.setValues);
  const validate = store((state) => state.validate);
  const setSchema = store((state) => state.setSchema);

  const getValues = React.useCallback(() => store.getState().values, []);
  const isValid = useMemo(
    () =>
      options?.strategy === 'single-field'
        ? Object.values(values).every((value) => value !== '')
        : Object.keys(errors).length === 0,
    [errors, options, values],
  );

  useEffect(() => {
    setSchema(options?.schema);
  }, []);

  return {
    values: values as V,
    errors: errors as Record<keyof V, string>,
    isValid,
    onFieldChange,
    getValues: getValues as () => V,
    setValues: setValues as (values: V) => void,
    changeHandlers: changeHandlers as CreateFormStore<V>['onChangesMap'],
    validate,
    setSchema,
  };
};
